import { useCallback } from 'react';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';
import { DataGlueGraph, DataConnection, NodeInstance, getDefaultValueForDataType } from '@glueapp/graph-types';
import { yDocMutator } from '@glueapp/graph-collab';
import { NODE_LIBRARY_DICTIONARY, createFunctionNodeInstance, createFunctionDefinition } from '@glueapp/graph-nodes';
import {
    layoutGraph as calculateLayout,
    findEnclosedNodes,
    LayoutGraph,
    LayoutNode,
    LayoutConnection,
    PortPositionsMap,
    applyPositionUpdatesWithCenterPreservation,
} from '@glueapp/graph-layout';
import { GraphReducerActions } from '../actions';
import { getHighestIdFromObjectsWithIds } from '../drawingLayer/helpers/getHighestIdFromObjectsWithIds';
import { dictionaryFromStringTuple } from '../helpers/dictionaryFromTuple';
import { isGraphCyclic } from '../isGraphCyclic';
import { subtractVectors } from '../helpers/Vector';
import { DRAW_CONFIG } from '../drawingLayer/drawConfig.constant';
import { awarenessSelection, computeBranchSelection, snapshotConnectionsInScope, snapshotNodesInScope } from './dispatchHelpers';

/**
 * The single dispatch chokepoint for all graph mutations.
 *
 * Phase 3 replaces the legacy reducer + 3-second throttled save with this. Every
 * `GraphReducerActions` value flows through here:
 *   - Selection actions update awareness (`selectedLegacyIds` field).
 *   - `*_END` drag/resize actions call `undoManager.stopCapturing()` to close
 *     the current undo group.
 *   - All other actions delegate to `yDocMutator.*`, with `parentFunctionId`
 *     pulled from `action.functionId`.
 *
 * IDs (legacy int) are computed from `getSnapshot()` here, not inside the
 * mutator, so the dispatcher uses the user-visible state (presentationGraph)
 * rather than reading the doc twice.
 */

interface DispatcherArgs {
    yDoc: Y.Doc | null;
    awareness: Awareness | null;
    undoManager: Y.UndoManager | null;
    getSnapshot: () => DataGlueGraph | undefined;
}

export function useYDocDispatch(args: DispatcherArgs): (action: GraphReducerActions) => void {
    const { yDoc, awareness, undoManager, getSnapshot } = args;

    return useCallback(
        (action: GraphReducerActions) => {
            if (!yDoc || !awareness) return;

            // Boundary handling: drag/resize endings close the current undo group.
            // For all other mutators, also call stopCapturing at the start so a typing
            // burst right after a drag doesn't merge into the drag's undo step.
            const isDragOrResizeEnd = action.type === 'MOVE_NODES_END' || action.type === 'RESIZE_NODE_END';
            const isContinuousDrag = action.type === 'MOVE_NODES' || action.type === 'RESIZE_NODE' || action.type === 'SET_NODE_DIMENSIONS';
            if (undoManager && !isDragOrResizeEnd && !isContinuousDrag) {
                undoManager.stopCapturing();
            }

            const snapshot = getSnapshot();
            const parentFunctionId = 'functionId' in action ? action.functionId : undefined;
            const opts = parentFunctionId ? { parentFunctionId } : undefined;

            switch (action.type) {
                case 'ADD_NODE': {
                    const template = NODE_LIBRARY_DICTIONARY[action.nodeTemplateId];
                    if (!template) return;
                    const targetNodes = snapshotNodesInScope(snapshot, parentFunctionId);
                    const newId = (getHighestIdFromObjectsWithIds(targetNodes) || 0) + 1;
                    const newNode: NodeInstance = {
                        id: newId,
                        nodeId: template.id,
                        inPorts: template.inPorts,
                        outPorts: template.outPorts,
                        selected: false,
                        portValues: dictionaryFromStringTuple(
                            template.inPorts.filter(port => !port.optional),
                            inPort => [inPort.name, getDefaultValueForDataType(inPort.type)],
                        ),
                        portUserDefinedTypes: {},
                        state: {},
                        position: subtractVectors(action.position, { x: 200, y: 100 }),
                    };
                    yDocMutator.addNode(yDoc, newNode, opts);
                    return;
                }

                case 'MERGE_IN_GRAPH': {
                    if (action.deselectOld) {
                        awareness.setLocalStateField('selectedLegacyIds', []);
                    }
                    yDocMutator.mergeInGraph(yDoc, action.nodes, action.connections, opts);
                    return;
                }

                case 'SET_NODE_STATE':
                    yDocMutator.setNodeState(yDoc, action.id, action.state, opts);
                    return;

                case 'SET_NODE_SHAPE':
                    // Shape is derived (`processGraph` reconstructs `inPorts`/`outPorts`
                    // from node templates + state). Keep the action present for legacy
                    // call-site compatibility; persist nothing.
                    return;

                case 'MOVE_NODES':
                    yDocMutator.moveNodes(yDoc, action.selectedLegacyIds, action.delta, opts);
                    return;

                case 'MOVE_NODES_END':
                case 'RESIZE_NODE_END':
                    if (undoManager) undoManager.stopCapturing();
                    return;

                case 'SET_PORT_VALUE_AND_TYPE':
                    yDocMutator.setPortValueAndType(
                        yDoc,
                        action.nodeInstanceId,
                        action.port.name,
                        action.value,
                        action.userDefinedType,
                        opts,
                    );
                    return;

                case 'SET_PORT_USER_DEFINED_TYPE':
                    yDocMutator.setPortUserDefinedType(yDoc, action.nodeInstanceId, action.portName, action.userDefinedType, opts);
                    return;

                case 'RESIZE_NODE':
                    // Reducer was already a no-op here; matched.
                    return;

                case 'REMOVE_NODES':
                    yDocMutator.removeNodes(yDoc, action.ids, opts);
                    return;

                case 'ADD_CONNECTION': {
                    if (!snapshot) return;
                    // Cycle check: simulate the addition against the snapshot before writing.
                    const targetConnections = snapshotConnectionsInScope(snapshot, parentFunctionId);
                    const probedConnections: DataConnection[] = [
                        ...targetConnections.filter(
                            connection =>
                                !(
                                    connection.receivingNodeInstanceId === action.connection.receivingNodeInstanceId &&
                                    connection.receivingPortName === action.connection.receivingPortName
                                ),
                        ),
                        { ...action.connection, id: -1 } as DataConnection,
                    ];
                    const probedGraph: DataGlueGraph = parentFunctionId
                        ? {
                              ...snapshot,
                              functions: snapshot.functions.map(fn =>
                                  fn.id === parentFunctionId ? { ...fn, connections: probedConnections } : fn,
                              ),
                          }
                        : { ...snapshot, connections: probedConnections };
                    if (isGraphCyclic(probedGraph)) return;

                    const nextLegacyId = (getHighestIdFromObjectsWithIds(targetConnections) || 0) + 1;
                    yDocMutator.addConnection(yDoc, action.connection, nextLegacyId, opts);
                    return;
                }

                case 'REMOVE_CONNECTION': {
                    const targetConnections = snapshotConnectionsInScope(snapshot, parentFunctionId);
                    const target = targetConnections[action.index];
                    if (!target) return;
                    yDocMutator.removeConnection(yDoc, target.id, opts);
                    return;
                }

                case 'REMOVE_CONNECTION_BY_ENDPOINTS':
                    yDocMutator.removeConnectionByEndpoints(
                        yDoc,
                        action.sendingNodeInstanceId,
                        action.sendingPortName,
                        action.receivingNodeInstanceId,
                        action.receivingPortName,
                        opts,
                    );
                    return;

                case 'SELECT_NODES': {
                    const current = new Set<number>(awarenessSelection(awareness));
                    for (const id of action.ids) current.add(id);
                    awareness.setLocalStateField('selectedLegacyIds', Array.from(current));
                    return;
                }

                case 'SELECT_BRANCH': {
                    const ids = computeBranchSelection(snapshot, parentFunctionId, action.id);
                    awareness.setLocalStateField('selectedLegacyIds', ids);
                    return;
                }

                case 'SELECT_BRANCH_ADD': {
                    const ids = computeBranchSelection(snapshot, parentFunctionId, action.id);
                    const current = new Set<number>(awarenessSelection(awareness));
                    for (const id of ids) current.add(id);
                    awareness.setLocalStateField('selectedLegacyIds', Array.from(current));
                    return;
                }

                case 'DESELECT_NODES': {
                    const current = new Set<number>(awarenessSelection(awareness));
                    for (const id of action.ids) current.delete(id);
                    awareness.setLocalStateField('selectedLegacyIds', Array.from(current));
                    return;
                }

                case 'SELECT_ONLY_ONE_NODE':
                    awareness.setLocalStateField('selectedLegacyIds', [action.id]);
                    return;

                case 'DESELECT_ALL_NODES':
                    awareness.setLocalStateField('selectedLegacyIds', []);
                    return;

                case 'SET_VARIABLES':
                    yDocMutator.setVariables(yDoc, action.variables);
                    return;

                case 'ADD_VARIABLE':
                    yDocMutator.addVariable(yDoc, action.newVariable);
                    return;

                case 'UPDATE_VARIABLE':
                    yDocMutator.updateVariable(yDoc, action.oldName, action.variable);
                    return;

                case 'DELETE_VARIABLE':
                    yDocMutator.deleteVariable(yDoc, action.name);
                    return;

                case 'SET_NODE_SELECTED_INTEGRATION_ACCESS':
                    yDocMutator.setNodeSelectedIntegrationAccess(yDoc, action.id, action.integrationAccess, opts);
                    return;

                case 'LAYOUT_GRAPH':
                    handleLayoutGraph(yDoc, action, snapshot, parentFunctionId, opts);
                    return;

                case 'SET_SELECTED_VARIABLE':
                    yDocMutator.setNodeSelectedVariable(yDoc, action.nodeInstanceId, action.name, opts);
                    return;

                case 'SET_NODE_DIMENSIONS':
                    yDocMutator.setNodeDimensions(yDoc, action.nodeId, action.dimensions, opts);
                    return;

                case 'ADD_FUNCTION':
                    yDocMutator.addFunction(yDoc, action.newFunction);
                    return;

                case 'UPDATE_FUNCTION':
                    yDocMutator.updateFunction(yDoc, action.functionId, action.updatedFunction);
                    return;

                case 'UPDATE_FUNCTION_NAME':
                    yDocMutator.updateFunctionName(yDoc, action.functionId, action.name);
                    return;

                case 'UPDATE_FUNCTION_INPUTS':
                    yDocMutator.updateFunctionInputs(yDoc, action.functionId, action.inputPortDefinitions);
                    return;

                case 'UPDATE_FUNCTION_OUTPUTS':
                    yDocMutator.updateFunctionOutputs(yDoc, action.functionId, action.outputPortDefinitions);
                    return;

                case 'DELETE_FUNCTION':
                    yDocMutator.deleteFunction(yDoc, action.functionId);
                    return;

                case 'SET_FUNCTION_PORT_VALUE':
                    yDocMutator.setFunctionPortValue(yDoc, action.nodeInstanceId, action.portName, action.referencedFunctionId, opts);
                    return;

                case 'CREATE_FUNCTION_WITH_INSTANCE': {
                    const targetNodes = snapshotNodesInScope(snapshot, parentFunctionId);
                    const newNodeId = (getHighestIdFromObjectsWithIds(targetNodes) || 0) + 1;
                    const newFunctionId = `func_${Date.now()}`;
                    const fn = createFunctionDefinition(newFunctionId, action.functionName, DRAW_CONFIG.BASE_SIZE / 2);
                    const instanceNode = createFunctionNodeInstance(
                        newNodeId,
                        newFunctionId,
                        action.functionName,
                        fn.inputPortDefinitions,
                        fn.outputPortDefinitions,
                        action.position,
                    );
                    yDocMutator.createFunctionWithInstance(yDoc, fn, instanceNode, opts);
                    return;
                }

                case 'CREATE_FUNCTION_FROM_SIGNATURE': {
                    const newFunctionId = `func_${Date.now()}`;
                    const fn = createFunctionDefinition(
                        newFunctionId,
                        action.functionName,
                        DRAW_CONFIG.BASE_SIZE / 2,
                        action.inputPortDefinitions,
                        action.outputPortDefinitions,
                    );
                    yDocMutator.createFunctionFromSignature(yDoc, fn, action.nodeInstanceId, action.portName, opts);
                    return;
                }

                case 'ADD_FUNCTION_INSTANCE': {
                    // ADD_FUNCTION_INSTANCE diverges from the rest: `functionId` is the
                    // referenced function, and `parentFunctionId` is the scope. So the
                    // top-level `parentFunctionId`/`opts` derived above is wrong for this
                    // case — re-derive from `action.parentFunctionId`.
                    const parent = action.parentFunctionId;
                    const parentOpts = parent ? { parentFunctionId: parent } : undefined;
                    const targetNodes = snapshotNodesInScope(snapshot, parent);
                    const newNodeId = (getHighestIdFromObjectsWithIds(targetNodes) || 0) + 1;
                    const referencedFn = snapshot?.functions?.find(fn => fn.id === action.functionId);
                    if (!referencedFn) return;
                    const instanceNode = createFunctionNodeInstance(
                        newNodeId,
                        action.functionId,
                        referencedFn.name,
                        referencedFn.inputPortDefinitions,
                        referencedFn.outputPortDefinitions,
                        action.position,
                    );
                    yDocMutator.addFunctionInstance(yDoc, instanceNode, parentOpts);
                    return;
                }

                default: {
                    // Exhaustiveness check — every action.type above must be handled.
                    const exhaustive: never = action;
                    void exhaustive;
                    return;
                }
            }
        },
        [yDoc, awareness, undoManager, getSnapshot],
    );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function handleLayoutGraph(
    yDoc: Y.Doc,
    action: Extract<GraphReducerActions, { type: 'LAYOUT_GRAPH' }>,
    snapshot: DataGlueGraph | undefined,
    parentFunctionId: string | undefined,
    opts: { parentFunctionId: string } | undefined,
): void {
    if (!snapshot) return;
    const nodes = snapshotNodesInScope(snapshot, parentFunctionId);
    const connections = snapshotConnectionsInScope(snapshot, parentFunctionId);
    const layoutNodes: LayoutNode[] = nodes.map(node => ({
        id: node.id,
        x: node.position.x,
        y: node.position.y,
        width: node.width || 200,
        height: node.height || 100,
    }));
    const layoutConnections: LayoutConnection[] = connections.map(conn => ({
        id: conn.id,
        sendingNodeId: conn.sendingNodeInstanceId,
        receivingNodeId: conn.receivingNodeInstanceId,
        sendingPortName: conn.sendingPortName,
        receivingPortName: conn.receivingPortName,
    }));
    const layoutGraphData: LayoutGraph = { nodes: layoutNodes, connections: layoutConnections };

    const selectedNodeIds = nodes.filter(node => node.selected).map(node => node.id);
    const nodeIdsToLayout = findEnclosedNodes(layoutGraphData, selectedNodeIds);

    const portPositionsMap: PortPositionsMap | undefined = action.portPositions
        ? new Map(
              connections.map(conn => {
                  const sendingNode = nodes.find(node => node.id === conn.sendingNodeInstanceId);
                  const receivingNode = nodes.find(node => node.id === conn.receivingNodeInstanceId);
                  if (!sendingNode || !receivingNode) {
                      return [conn.id, { sendingPortY: 0, receivingPortY: 0 }];
                  }
                  const sendingPorts = action.portPositions![sendingNode.id];
                  const receivingPorts = action.portPositions![receivingNode.id];
                  const sendingPortY = sendingPorts?.outPortPositions[conn.sendingPortName]
                      ? sendingPorts.outPortPositions[conn.sendingPortName].y - sendingNode.position.y
                      : (sendingNode.height || 100) / 2;
                  const receivingPortY = receivingPorts?.inPortPositions[conn.receivingPortName]
                      ? receivingPorts.inPortPositions[conn.receivingPortName].y - receivingNode.position.y
                      : (receivingNode.height || 100) / 2;
                  return [conn.id, { sendingPortY, receivingPortY }];
              }),
          )
        : undefined;

    const positionUpdates = calculateLayout(layoutGraphData, nodeIdsToLayout, portPositionsMap);
    if (positionUpdates.length === 0) return;

    const updatedNodes = applyPositionUpdatesWithCenterPreservation(positionUpdates, nodes, 200, 100);
    const finalUpdates = updatedNodes.map(node => ({
        legacyId: node.id,
        x: node.position.x,
        y: node.position.y,
    }));
    yDocMutator.layoutGraph(yDoc, finalUpdates, opts);
}
